import { appMachine } from '#app/app/appMachine'
import type { EquipmentTypeResponseItem } from '#app/shared/types/EquipmentTypes'
import { getViteEnvironmentValue, isStorybook } from '#app/shared/utils/storybook'
import { createActorContext } from '@xstate/react'

export enum AppEnv {
  PROD = 'PROD',
  DEV = 'DEV',
  LOCAL = 'LOCAL'
}

export type Environment = {
  /**
   * The current environment where this application is served from
   */
  APP_ENV: AppEnv
  /**
   *  DataDog environment
   */
  DD_ENV: string
  /**
   * DataDog backend service name
   */
  DD_SERVICE: string
  /**
   * DataDog service version number
   */
  DD_VERSION: string
  /**
   * GoogleMaps API public key
   */
  GOOGLE_API_KEY: string
  /**
   * LaunchDarkly configuration
   */
  LAUNCHDARKLY_CLIENT_ID: string
  /**
   * PubNub channel prefix
   */
  PUBNUB_CHANNEL_PREFIX: string
  /**
   * PubNub publish key
   */
  PUBNUB_PUBLISH_KEY: string
  /**
   * PubNub subscribe key
   */
  PUBNUB_SUBSCRIBE_KEY: string
  /**
   * Application version
   */
  VERSION: string
  /**
   * Auth0 application domain
   */
  AUTH0_DOMAIN: string
  /**
   * Auth0 client identifier
   */
  AUTH0_CLIENT_ID: string
  /**
   * Auth0 scope API identifier
   */
  API_IDENTIFIER: string
  /*
   * Override LaunchDarkly feature flags for testing, a JSON string of key-value pairs.
   */
  FEATURE_FLAG_OVERRIDES?: string
  /*
   * Courier Public Key
   */
  COURIER_CLIENT_KEY?: string
  /**
   * Courier Public Key
   */
  COURIER_CLIENT_TEST_KEY?: string
  /*
   * Posthog API key
   */
  POSTHOG_API_KEY?: string
}
/**
 * Use these default values
 */

const runningInStorybook = isStorybook()

export const DefaultEnvironmentContext: Environment = {
  APP_ENV: AppEnv.LOCAL,
  DD_ENV: 'local',
  DD_SERVICE: 'tn-app-web',
  DD_VERSION: 'unknown',
  GOOGLE_API_KEY: runningInStorybook
    ? getViteEnvironmentValue('GOOGLE_API_KEY')
    : 'not-set',
  LAUNCHDARKLY_CLIENT_ID: 'not-set',
  PUBNUB_CHANNEL_PREFIX: 'localdev-shared.',
  PUBNUB_PUBLISH_KEY: 'pub-c-ecf7f9f7-30a7-4326-befe-7196b87b8efe',
  PUBNUB_SUBSCRIBE_KEY: 'sub-c-688c827d-c904-4ee8-acf4-d90e85bbf023',
  AUTH0_DOMAIN: 'login-staging.truenorthfleet.com',
  AUTH0_CLIENT_ID: 'not-set',
  API_IDENTIFIER: 'https://local.truenorthfleet.com/',
  VERSION: 'unknown',
  FEATURE_FLAG_OVERRIDES: undefined,
  COURIER_CLIENT_KEY: undefined,
  COURIER_CLIENT_TEST_KEY: undefined,
  POSTHOG_API_KEY: undefined
}

export type AppStateData = {
  environment?: Environment
  equipmentTypes?: EquipmentTypeResponseItem[]
  error?: Error
  courierToken: string
}

/**
 * Global application machine logger, exposed for mocking in tests.
 * @param args
 */
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const machineLogger = (...args: any[]) => {
  console.log(...args)
}

/**
 * Application context.
 */
export const AppContext = createActorContext(appMachine, {
  logger: machineLogger
})

/**
 * Hook for accessing the Application context
 */
export const useAppContext = () => AppContext.useActorRef()

export const useGlobalEnvironment = (): Environment => {
  const globalService = useAppContext()
  const { context } = globalService.getSnapshot()
  const env = context.environment

  if (!env) {
    if (isStorybook()) {
      return DefaultEnvironmentContext
    }
    throw new Error('Global environment not configured.')
  }
  return env
}

export let GlobalEnvironment = DefaultEnvironmentContext

export const validateEnvironment = (env: Environment) => {
  if (!env?.API_IDENTIFIER) {
    throw new Error('Environment not valid.')
  }
}

export const validateGlobalEnvironment = () => {
  return validateEnvironment(GlobalEnvironment)
}

export const setGlobalEnvironment = (env: Environment) => {
  GlobalEnvironment = { ...env }
}

/**
 * Used during testing to reset global environment.
 */
export const resetGlobalEnvironment = () => {
  GlobalEnvironment = DefaultEnvironmentContext
}
